import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShopOnboardingCategory,
  ShopOnboardingID,
  ShopOnboardings,
  ShopOnboardingSteps,
} from '@prisma/client';
import { isSudoManager } from '../../utils/global/helper';
import { getBusinessOnboarding } from '../../services/business';
import useUser from '../global/useUser';

const isStepComplete = (
  step: ShopOnboardingSteps,
  onboardings: ShopOnboardings[],
) => {
  if (!step.isActive) return true;

  const onboarding = onboardings.find(
    onboardingStep => onboardingStep.stepId === step.id,
  );
  if (!onboarding) return false;

  return (
    (step.isSkippable &&
      (onboarding.status === 'SKIPPED' || onboarding.status === 'COMPLETE')) ||
    onboarding.status === 'COMPLETE'
  );
};

function useGetOnboarding() {
  const { user } = useUser();

  const isEnabled =
    !!user?.business?.id &&
    user.business.type === 'STUDIO' &&
    isSudoManager(user);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      'businessOnboarding',
      user?.business?.id,
      user?.business?.type,
      user?.role,
      user?.canViewReports,
    ],
    queryFn: () => getBusinessOnboarding(user?.business?.id!),
    enabled: isEnabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const categorySteps = useMemo(
    () =>
      data?.steps?.reduce(
        (categoriesMap, step) => {
          // eslint-disable-next-line no-param-reassign
          if (!categoriesMap[step.category]) categoriesMap[step.category] = [];
          categoriesMap[step.category].push(step);
          categoriesMap[step.category].sort((a, b) => a.order - b.order);
          return categoriesMap;
        },
        {} as Record<ShopOnboardingCategory, ShopOnboardingSteps[]>,
      ),
    [data],
  );

  const categoryPercentCompleted = useMemo(
    () =>
      Object.entries(categorySteps ?? {}).reduce(
        (categoriesMap, [category, steps]) => {
          const completedSteps = steps.filter(step =>
            isStepComplete(step, data?.onboardings ?? []),
          );
          const percentCompleted = Math.round(
            ((completedSteps.length || 0) / steps.length) * 100,
          );
          // eslint-disable-next-line no-param-reassign
          categoriesMap[category as ShopOnboardingCategory] = percentCompleted;
          return categoriesMap;
        },
        {} as Record<ShopOnboardingCategory, number>,
      ),
    [categorySteps, data],
  );

  const completedSteps = useMemo(
    () => data?.steps.filter(step => isStepComplete(step, data.onboardings)),
    [data],
  );

  const incompleteSteps = useMemo(
    () => data?.steps.filter(step => !isStepComplete(step, data.onboardings)),
    [data],
  );

  const isOnboardingComplete = useMemo(
    () => !!data?.steps.length && completedSteps?.length === data?.steps.length,
    [completedSteps?.length, data],
  );

  const percentCompleted = useMemo(() => {
    if (!data) return 0;
    return Math.round(
      ((completedSteps?.length || 0) / data.steps.length) * 100,
    );
  }, [completedSteps?.length, data]);

  const categoryCompleted = useMemo(
    () =>
      Object.entries(categoryPercentCompleted).reduce(
        (categoriesMap, [category, percentComp]) => {
          // eslint-disable-next-line no-param-reassign
          categoriesMap[category as ShopOnboardingCategory] =
            percentComp >= 100;
          return categoriesMap;
        },
        {} as Record<ShopOnboardingCategory, boolean>,
      ),
    [categoryPercentCompleted],
  );

  // Only reload if the step is not complete yet
  const reloadAfterUpdate = useCallback(
    async (stepId: ShopOnboardingID) => {
      if (isOnboardingComplete || !isEnabled) return;

      const isComplete = completedSteps?.some(s => s.id === stepId);
      if (isComplete) return;

      await refetch();
    },
    [completedSteps, isEnabled, isOnboardingComplete, refetch],
  );

  const getCategoryProgress = useCallback(
    (category: ShopOnboardingCategory) => {
      const stepsInCategory = categorySteps?.[category];
      const completedStepsInCategory = stepsInCategory?.filter(
        s => completedSteps?.some(completedStep => completedStep.id === s.id),
      );
      return {
        total: stepsInCategory?.length ?? 0,
        completed: completedStepsInCategory?.length ?? 0,
      };
    },
    [categorySteps, completedSteps],
  );

  return {
    steps: data?.steps || [],
    categories: categorySteps,
    categorySteps,
    categoryPercentCompleted,
    categoryCompleted,
    getCategoryProgress,
    onboardings: data?.onboardings || [],
    isOnboardingComplete,
    completedSteps,
    incompleteSteps,
    percentCompleted,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    reloadAfterUpdate,
    isEnabled,
  };
}

export default useGetOnboarding;
